import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, X, ScanLine, RefreshCw, CircleAlert } from "lucide-react";

/**
 * Camera-based QR scanner. Uses jsQR to decode camera frames.
 * Emits `onDetected(qrId)` when the scanned URL matches `/samples/scan/{qr_id}`
 * (or when the string is exactly an 8-char alnum id).
 *
 * Props:
 *  open, onClose, onDetected
 */
export default function QRScannerDialog({ open, onClose, onDetected }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [hint, setHint] = useState("Point the camera at a sample tag QR code…");
  const [lastRaw, setLastRaw] = useState(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);

  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line no-inner-declarations
    async function start() {
      setError(null);
      if (!navigator.mediaDevices?.getUserMedia) {
        setError("This browser doesn't support camera access. Try opening Ops IQ in Chrome/Safari on your phone.");
        return;
      }
      try {
        const constraints = deviceId
          ? { video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false }
          : { video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try { await videoRef.current.play(); } catch { /* Safari autoplay quirks */ }
        }
        // Enumerate cameras for switching
        try {
          const list = await navigator.mediaDevices.enumerateDevices();
          setDevices(list.filter((d) => d.kind === "videoinput"));
        } catch { /* silent */ }
        startLoop();
      } catch (e) {
        setError(e?.message?.includes("denied") ? "Camera permission denied. Enable camera access in your browser settings and try again." : (e?.message || "Unable to open camera."));
      }
    }
    start();
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deviceId]);

  const stop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const startLoop = async () => {
    // Import jsQR only when scanning to keep initial bundle light
    const jsQR = (await import("jsqr")).default;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d", { willReadFrequently: true });
    const tick = () => {
      const video = videoRef.current;
      if (!video || video.readyState !== video.HAVE_ENOUGH_DATA || !ctx) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      // Downscale for perf while keeping enough resolution to decode
      const maxDim = 720;
      const scale = Math.min(1, maxDim / Math.max(vw, vh));
      const w = Math.floor(vw * scale);
      const h = Math.floor(vh * scale);
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(video, 0, 0, w, h);
      const img = ctx.getImageData(0, 0, w, h);
      const code = jsQR(img.data, w, h, { inversionAttempts: "attemptBoth" });
      if (code?.data) {
        const raw = code.data.trim();
        setLastRaw(raw);
        const qrId = extractQrId(raw);
        if (qrId) {
          stop();
          onDetected?.(qrId);
          return;
        }
        setHint("Detected a QR but it isn't a sample tag — try another.");
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const switchCamera = () => {
    if (!devices.length) return;
    const idx = devices.findIndex((d) => d.deviceId === deviceId);
    const next = devices[(idx + 1) % devices.length];
    setDeviceId(next.deviceId);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { stop(); onClose?.(); } }}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-slate-900 border-slate-800" data-testid="qr-scanner-dialog">
        <DialogHeader className="p-3 border-b border-white/10 flex flex-row items-center gap-2 space-y-0">
          <ScanLine className="h-5 w-5 text-brand" />
          <DialogTitle className="text-white text-[15px] flex-1">Scan a sample tag</DialogTitle>
          <DialogDescription className="sr-only">Camera-based sample tag scanner. Point the camera at a sample QR to open its details.</DialogDescription>
          {devices.length > 1 && (
            <Button size="icon" variant="ghost" onClick={switchCamera} className="h-8 w-8 text-white hover:bg-white/10">
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </DialogHeader>

        <div className="relative bg-black aspect-[3/4] max-h-[70vh]">
          <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted />
          <canvas ref={canvasRef} className="hidden" />
          {/* Overlay: viewfinder */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-8 rounded-lg border-2 border-white/60 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]">
              <div className="absolute -top-0.5 -left-0.5 w-6 h-6 border-t-2 border-l-2 border-brand rounded-tl-lg" />
              <div className="absolute -top-0.5 -right-0.5 w-6 h-6 border-t-2 border-r-2 border-brand rounded-tr-lg" />
              <div className="absolute -bottom-0.5 -left-0.5 w-6 h-6 border-b-2 border-l-2 border-brand rounded-bl-lg" />
              <div className="absolute -bottom-0.5 -right-0.5 w-6 h-6 border-b-2 border-r-2 border-brand rounded-br-lg" />
              <div className="absolute inset-x-0 top-1/2 h-0.5 bg-brand/70 animate-pulse" />
            </div>
          </div>
          {error && (
            <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
              <div className="rounded-lg bg-slate-900/80 border border-rose-500/50 p-4 text-rose-200 text-[13px] flex flex-col items-center gap-2">
                <CircleAlert className="h-6 w-6 text-rose-400" />
                {error}
              </div>
            </div>
          )}
        </div>

        <div className="p-3 text-[12.5px] text-slate-200 bg-slate-900 flex items-center gap-2">
          <Camera className="h-4 w-4 text-brand shrink-0" />
          <span className="flex-1">{hint}</span>
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => { stop(); onClose?.(); }}>
            <X className="h-4 w-4 mr-1" /> Close
          </Button>
        </div>
        {lastRaw && (
          <div className="px-3 pb-3 text-[10.5px] font-mono text-slate-400 truncate">Last read: {lastRaw}</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// A QR may encode a full URL "…/samples/scan/{qr_id}", a plain-text sticker
// payload containing a line "QR: XXXXXXXX", or just the id itself.
export function extractQrId(raw) {
  if (!raw) return null;
  const s = raw.trim();
  // Plain-text sticker payload — extract from "QR: XXXX" line
  const qrLine = s.match(/(?:^|\n)\s*QR\s*[:=]\s*([A-Z0-9]{4,20})/i);
  if (qrLine) return qrLine[1].toUpperCase();
  // Legacy URL pattern
  const m = s.match(/\/samples\/scan\/([A-Z0-9]{4,20})/i);
  if (m) return m[1].toUpperCase();
  // Bare id fallback — 8-char alnum
  if (/^[A-Z0-9]{8}$/i.test(s)) return s.toUpperCase();
  return null;
}
